import { showDialogInfo } from "@/hooks/use-dialog-info"
import type { UndoRedoResponse } from "@/types/action"
import {
  ClientException,
  ClientValidationException,
} from "@/types/client-exceptions"
import type { DialogInfoContext } from "@/types/dialog"
import {
  ConfirmOption,
  ItineraryClearOnly,
  ItineraryEvent,
} from "@/types/itinerary"
import { TimeFrame, TripRef } from "@/types/trip"

import { updateChatOnCreateOrUpdateItineraryEvent } from "@/lib/chat"
import {
  buildCreateItineraryEventParams,
  buildUpdateItineraryEventParams,
  type PreparedAddItineraryEventAction,
  type PreparedUpdateItineraryEventAction,
} from "@/lib/itinerary-action-builders"
import { isSerializedClientError } from "@/lib/storage/api/base-query"
import { itineraryApi } from "@/lib/storage/api/itinerary-api"
import { removeEvents } from "@/lib/storage/itinerary-remove-events-thunks"
import { AppDispatch } from "@/lib/storage/redux-store"
import { hasFixedStartDate } from "@/lib/time-frame-utils"
import { toastManager } from "@/lib/toast"
import { isNumberArray } from "@/lib/utils/collections"

function getConfirmationRequiredData(
  error: unknown
): ConfirmationRequiredData | undefined {
  if (!isSerializedClientError(error, "ConfirmationRequired")) {
    return undefined
  }

  const { data } = error
  if (
    typeof data !== "object" ||
    data == null ||
    !("movable_event_ids" in data) ||
    !("extendable_event_ids" in data)
  ) {
    return undefined
  }

  const { movable_event_ids, extendable_event_ids } = data
  if (
    !isNumberArray(movable_event_ids) ||
    !isNumberArray(extendable_event_ids)
  ) {
    return undefined
  }

  return {
    movableEventIds: movable_event_ids,
    extendableEventIds: extendable_event_ids,
  }
}

export async function createPreparedItineraryEvent({
  dispatch,
  tripRef,
  tripTimeFrame,
  addAction,
}: CreatePreparedItineraryEventOptions): Promise<ItineraryEvent | undefined> {
  if (
    tripTimeFrame &&
    !hasFixedStartDate(tripTimeFrame) &&
    addAction.updateTripTimeFrame === undefined
  ) {
    const shouldSetTripDates = await confirmDialog({
      title: "Set trip dates?",
      message:
        "Your trip has flexible dates. Adding this event will set your trip dates.",
      cancelLabel: "Cancel",
      okLabel: "Yes, set dates",
      okVariant: "primary",
    })
    if (!shouldSetTripDates) {
      return undefined
    }

    try {
      return await createPreparedItineraryEventWithTimeFrame({
        dispatch,
        tripRef,
        tripTimeFrame,
        addAction,
        updateTimeFrame: true,
      })
    } catch {
      return undefined
    }
  }

  try {
    return await createPreparedItineraryEventWithTimeFrame({
      dispatch,
      tripRef,
      tripTimeFrame,
      addAction,
      updateTimeFrame: addAction.updateTripTimeFrame,
    })
  } catch (error) {
    if (
      isSerializedClientError(error, "ItineraryEventOutsideTimeFrame") ||
      isSerializedClientError(error, "ConfirmationRequired")
    ) {
      const shouldAdjustTripDates = await confirmAdjustTripDates()
      if (!shouldAdjustTripDates) {
        return undefined
      }

      try {
        return await createPreparedItineraryEventWithTimeFrame({
          dispatch,
          tripRef,
          tripTimeFrame,
          addAction,
          updateTimeFrame: true,
        })
      } catch (secondError) {
        console.error("Error adjusting trip timeline:", secondError)
        toastManager.add({
          type: "error",
          description:
            "Sorry, we ran into a problem adjusting your trip dates. Please adjust manually and try again.",
        })
        return undefined
      }
    }

    toastManager.add({
      type: "error",
      description: "Failed to add itinerary event",
    })
    return undefined
  }
}

async function createPreparedItineraryEventWithTimeFrame({
  dispatch,
  tripRef,
  addAction,
  updateTimeFrame,
}: CreatePreparedItineraryEventWithTimeFrameOptions): Promise<ItineraryEvent> {
  const response = await dispatch(
    itineraryApi.endpoints.createEvent.initiate({
      tripRef,
      params: buildCreateItineraryEventParams(addAction, {
        updateTimeFrame,
      }),
    })
  ).unwrap()

  updateChatOnCreateOrUpdateItineraryEvent(dispatch, response)
  return response.event
}

export async function updatePreparedItineraryEvent({
  dispatch,
  tripRef,
  tripTimeFrame,
  updateAction,
  confirmed,
  updateTimeFrame,
  moveConfirmed,
  onMoveConfirmed,
}: UpdatePreparedItineraryEventOptions): Promise<ItineraryEvent | undefined> {
  const requestedUpdateTimeFrame =
    updateTimeFrame ?? updateAction.updateTripTimeFrame

  if (
    tripTimeFrame &&
    !hasFixedStartDate(tripTimeFrame) &&
    updateAction.requestedScheduled === true &&
    updateAction.previous.scheduled === false &&
    requestedUpdateTimeFrame === undefined
  ) {
    const shouldSetTripDates = await confirmDialog({
      title: "Set trip dates?",
      message:
        "Your trip has flexible dates. Scheduling this event will set your trip dates.",
      cancelLabel: "Cancel",
      okLabel: "Yes, set dates",
      okVariant: "primary",
    })
    if (!shouldSetTripDates) {
      return undefined
    }

    try {
      return await updatePreparedItineraryEvent({
        dispatch,
        tripRef,
        tripTimeFrame,
        updateAction,
        confirmed,
        updateTimeFrame: true,
        moveConfirmed,
        onMoveConfirmed,
      })
    } catch {
      return undefined
    }
  }

  try {
    return await updatePreparedItineraryEventWithTimeFrame({
      dispatch,
      tripRef,
      tripTimeFrame,
      updateAction,
      confirmed,
      updateTimeFrame: requestedUpdateTimeFrame,
      moveConfirmed,
      onMoveConfirmed,
    })
  } catch (error) {
    if (isSerializedClientError(error, "ItineraryEventOutsideTimeFrame")) {
      const shouldAdjustTripDates = await confirmAdjustTripDates()
      if (!shouldAdjustTripDates) {
        return undefined
      }

      try {
        return await updatePreparedItineraryEvent({
          dispatch,
          tripRef,
          tripTimeFrame,
          updateAction,
          confirmed,
          updateTimeFrame: true,
          moveConfirmed,
          onMoveConfirmed,
        })
      } catch (secondError) {
        console.error("Error adjusting trip timeline:", secondError)
        toastManager.add({
          type: "error",
          description:
            "Sorry, we ran into a problem adjusting your trip dates. Please adjust manually and try again.",
        })
        return undefined
      }
    }

    const confirmationData = getConfirmationRequiredData(error)
    if (confirmationData) {
      const needsMoveConfirmation = confirmationData.movableEventIds.length > 0
      const needsExtendConfirmation =
        confirmationData.extendableEventIds.length > 0

      if (needsMoveConfirmation && confirmed !== "confirm_move") {
        if (moveConfirmed) {
          return await updatePreparedItineraryEvent({
            dispatch,
            tripRef,
            tripTimeFrame,
            updateAction,
            confirmed: "confirm_move",
            updateTimeFrame: requestedUpdateTimeFrame,
            moveConfirmed,
            onMoveConfirmed,
          })
        }

        const shouldMoveEvent = await confirmDialog({
          title: "Move event?",
          message: "You are about to move a booked event.",
          cancelLabel: "No, keep it",
          okLabel: "Yes, move",
          okVariant: "primary",
        })
        if (!shouldMoveEvent) {
          return undefined
        }

        onMoveConfirmed?.()
        try {
          return await updatePreparedItineraryEvent({
            dispatch,
            tripRef,
            tripTimeFrame,
            updateAction,
            confirmed: "confirm_move",
            updateTimeFrame: requestedUpdateTimeFrame,
            moveConfirmed: true,
            onMoveConfirmed,
          })
        } catch {
          return undefined
        }
      }

      if (needsExtendConfirmation && confirmed !== "confirm_extend") {
        const shouldExtendTrip = await confirmDialog({
          title: "Extend trip?",
          message: "The trip needs to be extended to include the event.",
          cancelLabel: "No, keep the trip dates",
          okLabel: "Yes, extend",
          okVariant: "primary",
        })
        if (!shouldExtendTrip) {
          return undefined
        }

        try {
          return await updatePreparedItineraryEvent({
            dispatch,
            tripRef,
            tripTimeFrame,
            updateAction,
            confirmed:
              moveConfirmed || confirmed === "confirm_move"
                ? "confirm_move"
                : "confirm_extend",
            updateTimeFrame: true,
            moveConfirmed,
            onMoveConfirmed,
          })
        } catch (secondError) {
          console.error("Error extending trip timeline:", secondError)
          toastManager.add({
            type: "error",
            description:
              "Sorry, we ran into a problem extending your trip dates. Please adjust manually and try again.",
          })
          return undefined
        }
      }

      if (!needsMoveConfirmation && !needsExtendConfirmation) {
        const shouldAdjustTripDates = await confirmAdjustTripDates()
        if (!shouldAdjustTripDates) {
          return undefined
        }

        try {
          return await updatePreparedItineraryEvent({
            dispatch,
            tripRef,
            tripTimeFrame,
            updateAction,
            confirmed,
            updateTimeFrame: true,
            moveConfirmed,
            onMoveConfirmed,
          })
        } catch (secondError) {
          console.error("Error adjusting trip timeline:", secondError)
          toastManager.add({
            type: "error",
            description:
              "Sorry, we ran into a problem adjusting your trip dates. Please adjust manually and try again.",
          })
          return undefined
        }
      }

      toastManager.add({
        type: "error",
        description: "Failed to update itinerary event",
      })
      return undefined
    }

    if (
      isSerializedClientError(error, "ClientValidationException") ||
      error instanceof ClientValidationException
    ) {
      toastManager.add({
        type: "error",
        description: error.message || "Failed to update itinerary event",
      })
      return undefined
    }

    if (
      isSerializedClientError(error, "ClientException") ||
      error instanceof ClientException
    ) {
      toastManager.add({
        type: "error",
        description: "Failed to update itinerary event",
      })
      return undefined
    }

    console.error("Unhandled changeEvent error:", error)
    toastManager.add({
      type: "error",
      description: "Failed to update itinerary event",
    })
    return undefined
  }
}

async function updatePreparedItineraryEventWithTimeFrame({
  dispatch,
  tripRef,
  updateAction,
  confirmed,
  updateTimeFrame,
}: UpdatePreparedItineraryEventOptions): Promise<ItineraryEvent> {
  const response = await dispatch(
    itineraryApi.endpoints.updateEvent.initiate({
      tripRef,
      eventId: updateAction.event.id,
      updates: buildUpdateItineraryEventParams(updateAction, {
        updateTimeFrame,
      }),
      confirmed,
    })
  ).unwrap()

  updateChatOnCreateOrUpdateItineraryEvent(dispatch, response)
  return response.event
}

function confirmAdjustTripDates(): Promise<boolean> {
  return confirmDialog({
    title: "Adjust trip dates?",
    message:
      "This event doesn't fit within your current trip dates. Would you like to adjust them to include it?",
    cancelLabel: "No, cancel",
    okLabel: "Yes, adjust dates",
    okVariant: "primary",
  })
}

function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    showDialogInfo({
      ...options,
      okAction: () => {
        resolve(true)
      },
      cancelAction: () => {
        resolve(false)
      },
    })
  })
}

export async function removeItineraryEventById({
  dispatch,
  eventId,
}: RemoveItineraryEventByIdOptions): Promise<boolean> {
  try {
    await dispatch(removeEvents([eventId])).unwrap()
    return true
  } catch (error) {
    console.error("Error removing itinerary event: ", error)
    return false
  }
}

export async function clearItinerarySection({
  dispatch,
  tripRef,
  only,
}: ClearItinerarySectionOptions): Promise<boolean> {
  try {
    await dispatch(
      itineraryApi.endpoints.clearItinerary.initiate({
        tripRef,
        only,
      })
    ).unwrap()
    return true
  } catch (error) {
    console.error(getClearItinerarySectionConsoleMessage(only), error)
    toastManager.add({
      type: "error",
      description: getClearItinerarySectionToastMessage(only),
    })
    return false
  }
}

export async function runUndoItinerary({
  dispatch,
  tripRef,
}: RunUndoRedoItineraryOptions): Promise<UndoRedoResponse> {
  return await runUndoRedoItinerary({
    execute: () =>
      dispatch(itineraryApi.endpoints.undoItinerary.initiate(tripRef)).unwrap(),
    loadingMessage: "Undoing…",
    errorLogMessage: "Error undoing itinerary event: ",
    errorToastMessage: "Failed to undo itinerary event",
    fallbackError: "error in undo of itinerary event",
  })
}

export async function runRedoItinerary({
  dispatch,
  tripRef,
}: RunUndoRedoItineraryOptions): Promise<UndoRedoResponse> {
  return await runUndoRedoItinerary({
    execute: () =>
      dispatch(itineraryApi.endpoints.redoItinerary.initiate(tripRef)).unwrap(),
    loadingMessage: "Redoing…",
    errorLogMessage: "Error redoing itinerary event: ",
    errorToastMessage: "Failed to redo itinerary event",
    fallbackError: "error in redo of itinerary event",
  })
}

async function runUndoRedoItinerary({
  execute,
  loadingMessage,
  errorLogMessage,
  errorToastMessage,
  fallbackError,
}: RunUndoRedoItineraryRequest): Promise<UndoRedoResponse> {
  let loadingToastId: string | undefined
  let loadingToastTimeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    loadingToastTimeoutId = setTimeout(() => {
      loadingToastId = toastManager.add({
        type: "loading",
        description: loadingMessage,
        timeout: 0,
      })
    }, DELAY_SHOW_UNDO_REDO_TOAST_INFO)
    return await execute()
  } catch (error) {
    console.log(errorLogMessage, error)
    toastManager.add({ type: "error", description: errorToastMessage })
    return buildUndoRedoErrorResponse(fallbackError)
  } finally {
    if (loadingToastTimeoutId) {
      clearTimeout(loadingToastTimeoutId)
    }
    if (loadingToastId) {
      toastManager.close(loadingToastId)
    }
  }
}

function buildUndoRedoErrorResponse(error: string): UndoRedoResponse {
  return {
    status: "error",
    error,
    last_action: false,
    actions: [],
  }
}

function getClearItinerarySectionConsoleMessage(
  only: ItineraryClearOnly | undefined
) {
  if (only === "events") {
    return "Error removing scheduled events: "
  }
  if (only === "ideas") {
    return "Error removing ideas: "
  }
  return "Error clearing all itinerary events: "
}

function getClearItinerarySectionToastMessage(
  only: ItineraryClearOnly | undefined
) {
  if (only === "events") {
    return "Failed to remove scheduled events"
  }
  if (only === "ideas") {
    return "Failed to remove ideas"
  }
  return "Failed to clear itinerary"
}

const DELAY_SHOW_UNDO_REDO_TOAST_INFO = 800

type ConfirmationRequiredData = {
  movableEventIds: number[]
  extendableEventIds: number[]
}

type ConfirmDialogOptions = Pick<
  DialogInfoContext,
  "title" | "message" | "cancelLabel" | "okLabel" | "okVariant"
>

export interface CreatePreparedItineraryEventOptions {
  dispatch: AppDispatch
  tripRef: TripRef
  tripTimeFrame?: TimeFrame
  addAction: PreparedAddItineraryEventAction
}

interface CreatePreparedItineraryEventWithTimeFrameOptions extends CreatePreparedItineraryEventOptions {
  updateTimeFrame?: boolean
}

export interface UpdatePreparedItineraryEventOptions {
  dispatch: AppDispatch
  tripRef: TripRef
  tripTimeFrame?: TimeFrame
  updateAction: PreparedUpdateItineraryEventAction
  confirmed?: ConfirmOption
  updateTimeFrame?: boolean
  moveConfirmed?: boolean
  onMoveConfirmed?: () => void
}

export interface RemoveItineraryEventByIdOptions {
  dispatch: AppDispatch
  eventId: number
}

export interface ClearItinerarySectionOptions {
  dispatch: AppDispatch
  tripRef: TripRef
  only?: ItineraryClearOnly
}

export interface RunUndoRedoItineraryOptions {
  dispatch: AppDispatch
  tripRef: TripRef
}

interface RunUndoRedoItineraryRequest {
  execute: () => Promise<UndoRedoResponse>
  loadingMessage: string
  errorLogMessage: string
  errorToastMessage: string
  fallbackError: string
}
